/**
 * Updated Authentication Service using UnifiedApiService
 * 
 * This service handles all authentication operations using the unified API
 * for consistent token management and automatic session expiration handling.
 */

import { User } from '../types/user';
import { enhancedTokenManager } from './EnhancedTokenManager';
import { unifiedApi } from './UnifiedApiService';

/**
 * Authentication service for handling user authentication and profile management
 */
class AuthService {
  /**
   * Login with email and password
   */
  async login(email: string, password: string, deviceName?: string): Promise<{
    access_token: string;
    user: User;
    expires_at: string;
  }> {
    try {
      const response = await unifiedApi.publicRequest('post', '/auth/login/', {
        email,
        password,
        device_name: deviceName || this.getDeviceName(),
      });
      
      const { access_token, user, expires_at } = response;
      
      // Initialize enhanced token manager
      enhancedTokenManager.setToken(access_token, expires_at);
      
      return response;
    } catch (error) {
      throw this.handleError(error, 'Login failed');
    }
  }

  /**
   * Register a new user
   */
  async register(userData: {
    email: string;
    password: string;
    password_confirm: string;
    first_name: string;
    last_name: string;
    church?: string;
    role?: string;
  }): Promise<{
    access_token: string;
    user: User;
    expires_at: string;
  }> {
    try {
      // Add device information
      const data = {
        ...userData,
        device_name: this.getDeviceName(),
      };
      
      const response = await unifiedApi.publicRequest('post', '/auth/register/', data);
      
      const { access_token, user, expires_at } = response;
      
      // Initialize enhanced token manager
      enhancedTokenManager.setToken(access_token, expires_at);
      
      return response;
    } catch (error) {
      throw this.handleError(error, 'Registration failed');
    }
  }

  /**
   * Logout the current user
   */
  async logout(): Promise<void> {
    try {
      await unifiedApi.post('/auth/logout/');
    } catch (error) {
      console.error('Logout API call failed:', error);
      // Continue with local logout even if API call fails
    } finally {
      enhancedTokenManager.clearToken();
    }
  }

  /**
   * Get the current user's profile
   */
  async getCurrentUser(): Promise<User> {
    try {
      const user = await unifiedApi.get<User>('/auth/users/me/');
      return user;
    } catch (error) {
      throw this.handleError(error, 'Failed to get user profile');
    }
  }

  /**
   * Update the current user's profile
   */
  async updateProfile(userData: Partial<User>): Promise<User> {
    try {
      const user = await unifiedApi.patch<User>('/auth/users/me/', userData);
      return user;
    } catch (error) {
      throw this.handleError(error, 'Failed to update profile');
    }
  }

  /**
   * Request a password reset email
   */
  async forgotPassword(email: string): Promise<void> {
    try {
      await unifiedApi.publicRequest('post', '/auth/password/reset/', { email });
    } catch (error) {
      throw this.handleError(error, 'Failed to request password reset');
    }
  }

  /**
   * Reset password with token
   */
  async resetPassword(token: string, uid: string, newPassword: string, newPasswordConfirm: string): Promise<void> {
    try {
      await unifiedApi.publicRequest('post', '/auth/password/reset/confirm/', {
        token,
        uid,
        new_password: newPassword,
        new_password_confirm: newPasswordConfirm,
      });
    } catch (error) {
      throw this.handleError(error, 'Failed to reset password');
    }
  }

  /**
   * Check if a user is authenticated
   */
  async isAuthenticated(): Promise<boolean> {
    try {
      // Check if token is valid
      if (!enhancedTokenManager.isTokenValid()) {
        return false;
      }
      
      // Verify token is valid by making a request
      await this.getCurrentUser();
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Get all active device sessions
   */
  async getDeviceSessions() {
    try {
      const sessions = await unifiedApi.get('/auth/sessions/');
      return sessions;
    } catch (error) {
      throw this.handleError(error, 'Failed to get device sessions');
    }
  }

  /**
   * Get current device session
   */
  async getCurrentSession() {
    try {
      const session = await unifiedApi.get('/auth/sessions/current/');
      return session;
    } catch (error) {
      throw this.handleError(error, 'Failed to get current session');
    }
  }

  /**
   * Revoke a specific device session
   */
  async revokeSession(sessionId: string) {
    try {
      await unifiedApi.post(`/auth/sessions/${sessionId}/revoke/`);
    } catch (error) {
      throw this.handleError(error, 'Failed to revoke session');
    }
  }

  /**
   * Revoke all other device sessions
   */
  async revokeAllSessions() {
    try {
      await unifiedApi.post('/auth/sessions/revoke-all/');
    } catch (error) {
      throw this.handleError(error, 'Failed to revoke all sessions');
    }
  }

  /**
   * Get user preferences
   */
  async getPreferences(): Promise<any> {
    try {
      const preferences = await unifiedApi.get('/auth/users/preferences/');
      return preferences;
    } catch (error) {
      throw this.handleError(error, 'Failed to get preferences');
    }
  }

  /**
   * Update user preferences
   */
  async updatePreferences(preferences: any, partial: boolean = true): Promise<any> {
    try {
      console.log('[authService] Updating preferences:', preferences, 'partial:', partial);
      const updatedPreferences = partial
        ? await unifiedApi.patch('/auth/users/preferences/', preferences)
        : await unifiedApi.put('/auth/users/preferences/', preferences);
      console.log('[authService] Preferences updated successfully:', updatedPreferences);
      return updatedPreferences;
    } catch (error) {
      console.error('[authService] Failed to update preferences:', error);
      throw this.handleError(error, 'Failed to update preferences');
    }
  }

  /**
   * Get current access token
   * This method is async to ensure proper token validity and refresh
   */
  async getAccessToken(): Promise<string | null> {
    return await enhancedTokenManager.getToken();
  }

  /**
   * Get authentication headers for API requests
   */
  async getAuthHeaders(): Promise<{ Authorization?: string }> {
    const token = await enhancedTokenManager.getToken();
    if (token) {
      return { Authorization: `Bearer ${token}` };
    }
    return {};
  }

  /**
   * Get a user-friendly device name
   */
  private getDeviceName(): string {
    const userAgent = navigator.userAgent;
    let browser = 'Unknown Browser';
    let os = 'Unknown OS';
    
    // Detect browser
    if (userAgent.indexOf('Chrome') > -1) {
      browser = 'Chrome';
    } else if (userAgent.indexOf('Firefox') > -1) {
      browser = 'Firefox';
    } else if (userAgent.indexOf('Safari') > -1) {
      browser = 'Safari';
    } else if (userAgent.indexOf('Edge') > -1 || userAgent.indexOf('Edg') > -1) {
      browser = 'Edge';
    } else if (userAgent.indexOf('MSIE') > -1 || userAgent.indexOf('Trident') > -1) {
      browser = 'Internet Explorer';
    }
    
    // Detect OS
    if (userAgent.indexOf('Windows') > -1) {
      os = 'Windows';
    } else if (userAgent.indexOf('Mac') > -1) {
      os = 'Mac';
    } else if (userAgent.indexOf('Linux') > -1) {
      os = 'Linux';
    } else if (userAgent.indexOf('Android') > -1) {
      os = 'Android';
    } else if (userAgent.indexOf('iPhone') > -1 || userAgent.indexOf('iPad') > -1) {
      os = 'iOS';
    }
    
    return `${browser} on ${os}`;
  }

  /**
   * Handle API errors
   */
  private handleError(error: any, defaultMessage: string): Error {
    if (error.response?.data?.detail) {
      return new Error(error.response.data.detail);
    }
    
    // Handle nested error format: {"error": {"message": "...", "code": "..."}}
    if (error.response?.data?.error?.message) {
      return new Error(error.response.data.error.message);
    }

    // Handle flat error format: {"message": "..."}
    if (error.response?.data?.message) {
      return new Error(error.response.data.message);
    }

    // Handle detail format: {"detail": "..."}
    if (error.response?.data?.detail) {
      return new Error(error.response.data.detail);
    }

    if (error.response?.data) {
      // Handle validation errors — only for string values
      const errors = Object.entries(error.response.data)
        .filter(([, v]) => typeof v === 'string' || Array.isArray(v))
        .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
        .join('; ');

      if (errors) {
        return new Error(errors);
      }
    }
    
    return new Error(defaultMessage);
  }
}

// Create and export a singleton instance
export const authService = new AuthService();

// Export getAuthHeaders as a standalone function for convenience
export const getAuthHeaders = async () => authService.getAuthHeaders();